// IntradayBidding 入札作成タブの「入札 1 行」 を表す frontend 表示型。
//
// design HTML `design/bidding/intraday.html` `#intraday-bid-table` が必要としている列を
// 表現する。 proto `BiddingIntradayOrderListItem` をベースにしつつ、 design が要求するが
// proto に **まだ存在しない** field を mock 用に追加している。 各 mock field には
// TODO(proto) コメントで proto 側に追加すべき field 名を併記する。
//
// 設計判断: SpotBidding の `models/spotBidRow.ts` と同じ「frontend 表示行 + TODO(proto)」
// パターンを踏襲する。 時間前は GC ごとに細かく確定するため `gcDeadline` (= コマ別 GC 締切)
// を持つのが spot との差分。
import type { Timestamp } from '@bufbuild/protobuf/wkt'
import type { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import { Side } from '@ebalance/proto/bfftypes/v1/side_pb'
import {
  BiddingApprovalState,
  type BiddingBidSendErrorCategory,
  type BiddingBidSendStatus,
  BiddingIntradayOrderStatus,
} from '@ebalance/proto/bffuser/v1/bidding_common_model_pb'
import type {
  BiddingIntradayOrderOrderMethod,
  BiddingIntradayOrderWithdrawFailureReason,
  BiddingIntradayOrderWithdrawState,
} from '@ebalance/proto/bffuser/v1/bidding_intraday_model_pb'

import type { BidIssuerType } from '~/models/bidIssuerType'
import { isIntradayMarketOrder } from '~/models/intradayOrderMethod'

/** 発注主体 (= 札発行元の業務区分)。 SpotBidIssuerType と同義。 */
// proto `BiddingIntradayOrder.issuer_type` (LicenseType) から写す (issue #5366)。
export type IntradayBidIssuerType = BidIssuerType

/** 入札 1 行の表示型。 */
export type IntradayBidRow = {
  // ----- proto 由来 (= 実 RPC でも取れる) -----
  id: string
  areaCode: AreaCode
  /** コマ番号 1〜48。 */
  timeCode: number
  direction: Side
  /**
   * 価格種別 (指値 / 成行、 `intraday-bidding.md` AC-BIN-004d)。 SpotBidRow と同じ
   * 「undefined / UNSPECIFIED = 指値扱い」規約 (判定は [[isMarketOrderRow]] を通す)。
   */
  orderMethod?: BiddingIntradayOrderOrderMethod
  /**
   * 入札価格 円/kWh。 proto google.type.Decimal は string で受ける。
   * 成行 (`orderMethod === MARKET`) の注文は価格を持たず "0" を運ぶ。 この "0" は
   * 指値 0 円ではないので、 価格表示は必ず [[isMarketOrderRow]] で成行を除いてから行う。
   */
  priceYenPerKwh: string
  /** 入札量 MW。 */
  volumeMw: string
  bidStatus: BiddingIntradayOrderStatus
  /**
   * 承認状態 (一覧 RPC の `approval_state` のまま)。 ステータス列 (AC-BIN-035) の
   * 「承認待ち」「差戻し」を導く軸で、 独立した承認列は持たない。
   */
  approvalState: BiddingApprovalState
  /** 取り下げ済みか ([[isWithdrawnOrder]] を変換時に評価した値)。 */
  withdrawn: boolean

  /**
   * 送信軸 (= eSLJ 送信状態)。 提出可否判定に使う (= UNSPECIFIED = 未提出、 それ以外 = 提出済)。
   * 一覧 RPC の `BiddingIntradayOrderListItem.send_status` から流す。
   */
  sendStatus?: BiddingBidSendStatus

  /**
   * 送信失敗理由カテゴリ / 生 detail (= sendStatus が失敗系のときのみ意味を持つ)。
   * 一覧 RPC の `BiddingIntradayOrderListItem.send_error_category` /
   * `.send_error_detail` から流す (AC-BIN-059、`SendErrorReasonTooltip` の入力)。
   */
  sendErrorCategory?: BiddingBidSendErrorCategory
  sendErrorDetail?: string

  /** 板取消の進行状態 (AC-BIN-008b)。 一覧 RPC の `withdraw_state` から流す。 */
  withdrawState?: BiddingIntradayOrderWithdrawState

  /** 板取消の失敗確定の理由 (AC-BIN-008b)。 一覧 RPC の `withdraw_failure_reason` から流す。 */
  withdrawFailureReason?: BiddingIntradayOrderWithdrawFailureReason

  /**
   * 取下 (板取消 AC-BIN-008 / 予約取消 AC-BIN-093) を今受け付けるか。 一覧 RPC の
   * `withdrawable` (backend が状態と GC から判定) をそのまま流し、 取下ボタンはこの値で出す。
   */
  withdrawable: boolean

  /**
   * 「下書きに戻す」 (AC-BIN-115) を今受け付けるか。 一覧 RPC の `returnable_to_draft` (backend が
   * 送信中・GC 前・確定済ワークフロー経由でないことから判定) をそのまま流し、 ボタンはこの値で出す。
   */
  returnableToDraft: boolean

  /**
   * 確定済ワークフロー経由の札か (AC-BIN-050)。 一覧 RPC の `from_completed_workflow` をそのまま流し、
   * 行の下書き削除はこの値で非活性にする。
   */
  fromCompletedWorkflow?: boolean

  /**
   * GC を過ぎて JEPX が受け付けたかを確かめている札か (AC-BIN-115)。 一覧 RPC の
   * `receipt_checking` をそのまま流し、 送信中のバッジの横の補足はこの値で出す
   * (ステータスは変えない)。
   */
  receiptChecking?: boolean

  /**
   * 承認・差戻し (AC-BIN-095) を今受け付けるか。 一覧 RPC の `approval_actionable` (backend が
   * 状態と GC から判定、 AC-BIN-099) をそのまま流し、 承認待ちの行の「承認する」「差戻し」は
   * この値が false の時に無効化する。
   */
  approvalActionable: boolean

  /** 楽観ロック用の更新時刻 (SUBMIT = 状態遷移の `expected_updated_at`)。 */
  updatedAt?: Timestamp

  /** 更新者 (= 最終更新列のホバー tooltip、 AC-BIN-003)。 */
  updatedBy?: string
  /** 作成日時 (= 最終更新列のホバー tooltip、 AC-BIN-003)。 */
  createdAt?: Timestamp
  /** 作成者 (= 最終更新列のホバー tooltip、 AC-BIN-003)。 */
  createdBy?: string

  /**
   * サーバー由来の GC 時刻 (proto `Timestamp` のまま)。 承認 / 差戻しの操作可否
   * (AC-BIN-099) はこの値と現在時刻で判定し、 コマ番号からの近似を使わない。
   */
  gcAt?: Timestamp

  /** 申請者 (REQUEST 遷移時の操作者)。 AC-BIN-098 の自己承認の注意表示に使う。 */
  requestedBy?: string

  /**
   * 承認待ちの札が今待っている承認段階の承認者 (承認申請時に写した段)。 AC-BIN-098 の
   * 承認者非該当の注意表示に使う。
   */
  currentStageApproverMemberIds?: readonly string[]

  /**
   * 札を作成したワークフローの業務ID。 ワークフローを経由せずに作った札は undefined
   * (入札一覧のワークフローフィルタ AC-BIN-018 / ワークフローID 列 AC-BIN-003 で使用)。
   */
  workflowId?: string

  /** 約定価格 円/kWh。 部分約定を含む加重平均約定価格。 未約定は未設定 (AC-BIN-003)。 */
  matchedPriceYenPerKwh?: string
  /** 最終約定時刻 (札の約定のうち最も遅い約定日時。 約定が無い札は未設定、 AC-BIN-024)。 */
  lastMatchedAt?: Timestamp

  /** 残数量 MW。 注文量 − 約定量 (AC-BIN-003)。 */
  remainingVolumeMw?: string

  /**
   * 取下予定日時。 自動取消 (AC-BIN-017) の予約時刻。 取下予定がある入札のみ設定
   * (AC-BIN-003)。
   */
  withdrawAt?: Timestamp

  /**
   * JEPX 受渡契約コード (AC-BIN-003 の一覧列)。 eBalance で作った注文は作成時に決めて保存した
   * コード、 取り込んだ札 (AC-BIN-106) は取り込み元のコードそのもの。 保存前の旧い注文は未設定。
   */
  deliveryContractCd?: string

  /** メモ（内部用の自由記述、 AC-BIN-003 の一覧列）。 */
  memoFree?: string

  /** JEPX自由記述欄（AC-BIN-003 の一覧列）。 */
  memoJepx?: string

  /**
   * 取り込み元で見つからなくなった入札中の札か (AC-BIN-127)。 一覧 RPC の `needs_review` のまま。
   * ステータスは変わらず、 入札一覧に「要確認」の印を出し、 一部約定の札は GC 後も入札中で出す。
   */
  needsReview?: boolean
  /** 要確認の印が付いた時刻。 `needsReview` が true の札だけ設定される。 */
  needsReviewSince?: Timestamp

  /** JEPX の入札 ID。 backend が値を持つ札だけ設定され、 無い札は未設定 (AC-BIN-003)。 */
  jepxBidNo?: string

  // ----- TODO(proto): 以下は proto に追加される予定の field、 現状 converter で定数埋め -----

  /**
   * コマ別 GC 締切時刻表示 ("HH:MM")。 時間前市場はコマごとに GC を迎えるため行単位で持つ。
   * TODO(proto): BiddingIntradayOrder.gc_at (google.protobuf.Timestamp) から導出する。
   */
  gcDeadline: string

  /**
   * 約定量 MW。 GC 後 (= 約定 / 部分約定) のみ値が入る。 部分約定の場合は volumeMw より小さい。
   * TODO(proto): BiddingIntradayOrder.exec_volume_mw (google.type.Decimal、 optional)。
   */
  execVolumeMw?: string

  /** 発注主体 (= proto `issuer_type` 由来)。 */
  issuerType: IntradayBidIssuerType
}

/**
 * 注文が成行 (= 価格を指定しない注文、 AC-BIN-004d) かを判定する唯一の口。
 * 実体は共有の [[isIntradayMarketOrder]] (売買逆転の判定も同じ関数を使う)。
 */
export const isMarketOrderRow = isIntradayMarketOrder

/**
 * 取り下げ済みの札か (#8728)。 取下前の約定で部分約定 / 全約定に確定した札は約定軸だけでは
 * 板にある札と区別できないため、 backend `Withdraw` が立てる承認軸 CANCELLED でも判定する。
 */
export const isWithdrawnOrder = (order: {
  bidStatus: BiddingIntradayOrderStatus
  approvalState: BiddingApprovalState
}): boolean =>
  order.bidStatus === BiddingIntradayOrderStatus.CANCELLED ||
  // 自動取消 (GC で決めた形は承認軸が SUBMITTED のまま)。 約定ゼロの終端で、 取消と同じ側。
  order.bidStatus === BiddingIntradayOrderStatus.AUTO_CANCELLED ||
  order.approvalState === BiddingApprovalState.CANCELLED

/** [[isWithdrawnOrder]] の IntradayBidRow 版。 */
export const isWithdrawnBidRow = (row: IntradayBidRow): boolean => row.withdrawn
